<template>
  <app-view-default-template class="app-view-default-template-news">

    <!-- Title component -->
    <app-content-title label="News"> </app-content-title>

    <b-row>
      <b-col>
        <subscribe-link-button></subscribe-link-button>
      </b-col>
    </b-row>

    <!-- Search field -->
    <div class="filters-wrapper">
      <b-row>
        <b-col xs="12" sm="6" md="7" lg="8">
          <div class="input-group-wrapper">
            <b-input-group size="md">
              <b-form-input v-model="searchText" v-on:keyup.enter="search" id="search" type="text" placeholder="Search on title" autocomplete="off"/>

              <b-input-group-append>
                <b-button variant="outline-primary" v-on:click="search">Search</b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
        </b-col>

        <b-col xs="12" sm="6" md="5" lg="4">
          <b-input-group size="md">
            <b-form-select v-model="regulatoryFilter.selected" :options="regulatoryFilter.options" size="md" @change="regulatoryFilterOnChange()"></b-form-select>

            <b-input-group-append>
              <b-button variant="outline-secondary" v-on:click="resetSearch">Clear</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>
      </b-row>
    </div>

    <!-- Items -->
    <structure v-if="releases">
      <template v-slot:highlighted>
        <div v-for="(release, index) in releasesGetOne" v-bind:key="index" v-vpshow>
          <release-item-big :release="release" :targetElm="releaseItem"/>
        </div>
      </template>

      <template v-slot:list>
        <div v-for="(release, index) in releasesSmallList" v-bind:key="index" v-vpshow>
          <release-item-small :release="release"/>
        </div>
      </template>

      <template v-slot:rest>
        <b-col xs="12" sm="6" v-for="(release, index) in releasesBigList" v-bind:key="index" v-vpshow>
          <release-item-big :release="release" :targetElm="releaseItem"/>
        </b-col>
      </template>

      <template v-slot:releasesSimpleList>
        <b-col xs="12" sm="6" v-for="(release, index) in releases" v-bind:key="index" v-vpshow>
          <release-item-big :release="release"/>
        </b-col>
      </template>
    </structure>

    <!-- Infinite scroll component -->
    <infinite-loading :identifier="infiniteId" @infinite="infiniteHandler" :distance="20">
      <template slot="no-more">
        <p>You've reached the end!</p>
      </template>

      <template slot="no-results">
        <p>Unfortunately no results.</p>
      </template>

      <template slot="error">
        <p>
          Oops, unfortunately something went wrong! :(
        </p>

        <app-button-primary text="Try again" v-on:click.native="resetSearch"></app-button-primary>
      </template>
    </infinite-loading>

  </app-view-default-template>
</template>

<script>
  // Template
  import AppViewDefaultTemplate from '@/components/layout/templates/view/AppViewTemplateDefault';
  import AppContentTitle from '@/components/ui/titles/AppContentTitle';

  // Components
  import Structure from '@/views/news/components/Structure';
  import ReleaseItemBig from '@/views/news/components/ReleaseItemBig';
  import ReleaseItemSmall from '@/views/news/components/ReleaseItemSmall';
  import InfiniteLoading from 'vue-infinite-loading';

  // Buttons
  import AppButtonPrimary from '@/components/ui/buttons/AppButtonPrimary';
  import SubscribeLinkButton from '@/components/ui/buttons/SubscribeLinkButton';

  // Api's
  import NewsFeedApi from "@/services/news-feed";

  // Mixins
  import {mixinDatesHandler} from '@/mixins/dates-handler';

  // Configs
  import NewsFeedConfig from '@/configs/news-feed';
  import DatesConfig from "@/configs/dates";

  const IPO_START_DATE_FORMAT   = DatesConfig.IPO_START_DATE_FORMAT;
  const API_REQUEST_DATE_FORMAT = DatesConfig.API_REQUEST_DATE_FORMAT;
  const IPO_START_DATE          = process.env.VUE_APP_IPO_START_DATE;

  export default {
    name: 'News',

    mixins: [mixinDatesHandler],

    components: {
      AppViewDefaultTemplate, Structure, ReleaseItemBig, ReleaseItemSmall, InfiniteLoading, AppContentTitle, 
      AppButtonPrimary, SubscribeLinkButton,
    },

    data: function () {
      return {
        // Api
        releases: [],
        releasesTotalLength: 0,
        pageIndex: NewsFeedConfig.INDEX_API,
        searchText: null, // Defaults to today's date
        pageSize: NewsFeedConfig.PAGE_SIZE_API,
        pageSizeDefault: NewsFeedConfig.PAGE_SIZE_API,

        // History
        releaseItem: null,

        // Infinite scroll
        infiniteId: 1,

        // Filter
        regulatoryFilter: {
          selected: null,
          options: [
            { value: null, text: 'Filter by All' },
            { value: 'regulatory', text: 'Filter by Regulatory' },
            { value: 'non-regulatory', text: 'Filter by Non-regulatory' },
          ],
        }
      }
    },

    methods: {
      regulatoryFilterOnChange() {
        this.search();
      },

      search() {
        this.resetQueryParams();

        this.pageIndex  = 0;
        this.releases   = [];
        this.infiniteId += 1;

        this.queryHandler();

        this.triggerInfiniteLoader();
      },

      resetSearch() {
        this.resetQueryParams();

        this.pageIndex  = 0;
        this.releases   = [];
        this.searchText = null;
        this.regulatoryFilter.selected = null;

        this.queryHandler();

        this.triggerInfiniteLoader();
      },

      resetQueryParams() {
        this.releaseItem = null;

        this.$router.push({
          query: {}
        }).catch(()=>{});
      },

      infiniteHandler($state) {
        this.pageIndex += 1;

        const data = {
          params: {
            startDate: this.$moment(IPO_START_DATE, IPO_START_DATE_FORMAT).format(API_REQUEST_DATE_FORMAT),
            endDate: null,
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
            searchTerm: this.searchText,
          }
        };

        if (this.regulatoryFilter.selected) {
          Object.assign(data.params, { Regulatory: this.regulatoryValue })
        }

        this.resetViewHandler();

        let _this = this;
        new NewsFeedApi(data, {}).get(function (response) {
          if (response.status !== 200) {
            $state.error();
            return;
          }

          // Increase page
          _this.releasesTotalLength = response.data.TotalFoundReleases;

          // Append new data
          _this.releases.push(...response.data.Releases);

          if (_this.releases.length < _this.releasesTotalLength) {
            $state.loaded();
            return;
          }

          if (_this.releases.length !== 0) {
            $state.loaded();
          }

          $state.complete();
        });
      },

      queryHandler() {
        const pageIndex   = this.pageIndex;
        const releaseItem = this.releaseItem;
        const searchQuery = this.searchText;
        const regulatory  = this.regulatoryValue;

        const query = {
          index: pageIndex,
          query: searchQuery,
          release: releaseItem,
          regulatory: regulatory,
        };

        if (!regulatory) {
          delete query.regulatory;
        }

        if (!releaseItem) {
          delete query.release;
        }

        if (pageIndex <= 0) {
          delete query.index;
        }

        if (!searchQuery) {
          delete query.query;
        }

        this.$router.push({
          query: query
        }).catch(()=>{});
      },

      triggerInfiniteLoader() {
        this.infiniteId += 1;
      },

      viewHandler() {
       const pageIndex   = this.pageIndexUrlParam;
       const release     = this.releaseUrlParam;
       const searchQuery = this.searchQueryUrlParam;
       const regulatory  = this.regulatoryUrlParam;

       this.releaseItem = release;
       this.searchText  = searchQuery;
       this.regulatoryFilter.selected = regulatory;

       // Calculate page size
       this.pageSize = this.pageSize * pageIndex;

       this.triggerInfiniteLoader();
      },

      resetViewHandler() {
        if (this.pageSize !== this.pageSizeDefault) {
          this.pageSize  = this.pageSizeDefault;
          this.pageIndex = parseInt(this.$route.query.index);
        }
      },
    },

    computed: {
      releasesGetOne() {
        if (!this.releases) {
          return [];
        }

        return this.releases.filter((x, i) => {
          return i === 0;
        })
      },

      releasesSmallList() {
        if (!this.releases) {
          return [];
        }

        return this.releases.filter((x, i) => {
          return i > 0 && i <= 4;
        })
      },

      releasesBigList() {
        if (!this.releases) {
          return [];
        }

        return this.releases.filter((x, i) => {
          return i > 4;
        })
      },

      pageIndexUrlParam() {
        let index = this.$route.query.index;

        if (!parseInt(index)) {
          return 1;
        }

        return parseInt(index);
      },

      regulatoryUrlParam() {
        let regulatory = this.$route.query.regulatory;

        if (regulatory === 'true') {
          return 'regulatory';
        } else if (regulatory === 'false') {
          return 'non-regulatory';
        }

        return null;
      },

      searchQueryUrlParam() {
        let searchQuery = this.$route.query.query;

        if (!searchQuery) {
          return null;
        }

        return searchQuery;
      },

      releaseUrlParam() {
        let release = this.$route.query.release;

        if (!release) {
          return null;
        }

        return release;
      },

      regulatoryValue() {
        const regulatory = this.regulatoryFilter.selected;

        if (regulatory === 'regulatory') {
          return 'true';
        } else if (regulatory === 'non-regulatory') {
          return 'false';
        }

        return null;
      },
    },

    created() {
      window.scrollTo({
        top: 0,
      });
    },

    mounted() {
      this.viewHandler();
    },

    watch: {
      'pageIndex': function () {
        this.queryHandler();
      },
    }
  }
</script>

<style scoped lang="scss">
  .app-view-default-template-news {
    background-color: white;
    min-height: calc(100vh - 100px);

    .title-wrapper {
      margin-bottom: 20px;
    }

    .input-group-wrapper {
      width: 100%;
      margin-bottom: 20px;
    }

    .input-group-append {
      button {
        min-width: 75px;
      }
    }

    .btn-outline-primary {
      background-color: #60be6f;
      border-color: #60be6f;
      color: white;
      z-index: 0;

      &:hover, &:focus, &:active, &:visited {
        background-color: #4d9e59;
        border-color: #4d9e59;
      }
    }

    .btn-outline-secondary {
      background-color: transparent;
      border-color: #cecece;
      color: black;
      z-index: 0;

      &:hover, &:focus, &:active, &:visited {
        background-color: black;
        color: white;
        outline: none;
      }
    }

    .app-content-template-default, .app-content-template-default-inner {
      min-height: calc(100vh - 100px);
    }

    .filters-wrapper {
      width: 100%;
      margin-bottom: 40px;
    }

    @media (min-width: 576px) {
      .input-group-wrapper {
        margin-bottom: 0;
      }
    }
  }
</style>
